import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.js';
import AdminSectionLayout from '../components/AdminSectionLayout';
import CustomDialog from '../components/CustomDialog';
import { axiosInstance } from '../context/AuthContext';

export default function GalleryConfigAdmin() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [galleryConfigs, setGalleryConfigs] = useState([]);
  const [artworks, setArtworks] = useState([]); // Initialize as empty array
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    galleryId: '',
    galleryName: '',
    location: '',
    devices: []
  });

  useEffect(() => {
    if (user && !user.permissions.includes('admin')) {
      navigate('/home');
    }
    fetchGalleryConfigs();
    fetchArtworks();
    fetchGalleries();
  }, [user, navigate]);

  const fetchGalleryConfigs = async () => {
    try {
      const response = await axiosInstance.get('/gallery-config');
      setGalleryConfigs(response.data);
    } catch (error) {
      console.error('Error fetching gallery configs:', error);
    }
  };

  const fetchArtworks = async () => {
    try {
      const response = await axiosInstance.get('/artworks');
      const data = response.data;
      console.log('Artworks API response:', data); // Debug log
      // Handle different possible response structures
      if (Array.isArray(data)) {
        setArtworks(data);
      } else if (data.artworks && Array.isArray(data.artworks)) {
        setArtworks(data.artworks);
      } else if (data.data && Array.isArray(data.data)) {
        setArtworks(data.data);
      } else {
        console.error('Unexpected artworks response structure:', data);
        setArtworks([]);
      }
    } catch (error) {
      console.error('Error fetching artworks:', error);
      setArtworks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleries = async () => {
    try {
      const response = await axiosInstance.get('/gallery');
      const data = response.data;
      if (data.galleries) {
        setGalleries(data.galleries);
      }
    } catch (error) {
      console.error('Error fetching galleries:', error);
    }
  };

  const openDialog = (title, message, onConfirm) => {
    setDialogData({ title, message, onConfirm });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const validateConfig = async (galleryId) => {
    try {
      const response = await axiosInstance.get(`/gallery-config/${galleryId}/validate`);
      const data = response.data;
      
      openDialog(
        'Gallery Validation Results',
        `
        Gallery: ${data.galleryName}
        Total Devices: ${data.summary.totalDevices}
        Active Devices: ${data.summary.activeDevices}
        Recent Sessions: ${data.summary.totalSessions}
        
        Device Details:
        ${data.devices.map(device => 
          `• ${device.macAddress} (${device.deviceName || 'Unnamed'}): ${device.recentSessions} sessions${device.hasRecentData ? ' ✓' : ' ⚠️'}`
        ).join('\n')}
        `,
        () => closeDialog()
      );
    } catch (error) {
      console.error('Error validating config:', error);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      galleryId: '',
      galleryName: '',
      location: '',
      devices: [{
        macAddress: '',
        deviceName: '',
        cameras: [{
          cameraId: 0,
          artworkCameraId: '',
          isActive: true
        }],
        isActive: true
      }]
    });
    setEditingConfig(null);
    setShowCreateForm(true);
  };

  const handleEdit = (config) => {
    setFormData({
      galleryId: config.galleryId,
      galleryName: config.galleryName,
      location: config.location || '',
      devices: config.devices || []
    });
    setEditingConfig(config._id);
    setShowCreateForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axiosInstance.post('/gallery-config', formData);

      fetchGalleryConfigs();
      setShowCreateForm(false);
      openDialog(
        'Success',
        `Gallery configuration ${editingConfig ? 'updated' : 'created'} successfully!`,
        () => closeDialog()
      );
    } catch (error) {
      console.error('Error saving config:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save configuration';
      openDialog('Error', errorMessage, () => closeDialog());
    }
  };

  const addDevice = () => {
    setFormData(prev => ({
      ...prev,
      devices: [...prev.devices, {
        macAddress: '',
        deviceName: '',
        cameras: [{
          cameraId: 0,
          artworkCameraId: '',
          isActive: true
        }],
        isActive: true
      }]
    }));
  };

  const removeDevice = (deviceIndex) => {
    setFormData(prev => ({
      ...prev,
      devices: prev.devices.filter((_, index) => index !== deviceIndex)
    }));
  };

  const addCamera = (deviceIndex) => {
    setFormData(prev => ({
      ...prev,
      devices: prev.devices.map((device, index) => 
        index === deviceIndex 
          ? {
              ...device,
              cameras: [...device.cameras, {
                cameraId: device.cameras.length,
                artworkCameraId: '',
                isActive: true
              }]
            }
          : device
      )
    }));
  };

  const removeCamera = (deviceIndex, cameraIndex) => {
    setFormData(prev => ({
      ...prev,
      devices: prev.devices.map((device, index) => 
        index === deviceIndex 
          ? {
              ...device,
              cameras: device.cameras.filter((_, camIndex) => camIndex !== cameraIndex)
            }
          : device
      )
    }));
  };

  const updateDevice = (deviceIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      devices: prev.devices.map((device, index) => 
        index === deviceIndex ? { ...device, [field]: value } : device
      )
    }));
  };

  const updateCamera = (deviceIndex, cameraIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      devices: prev.devices.map((device, dIndex) => 
        dIndex === deviceIndex 
          ? {
              ...device,
              cameras: device.cameras.map((camera, cIndex) => 
                cIndex === cameraIndex ? { ...camera, [field]: value } : camera
              )
            }
          : device
      )
    }));
  };

  const getArtworkForGallery = (galleryId) => {
    // Ensure artworks is an array before filtering
    if (!Array.isArray(artworks)) {
      console.warn('Artworks is not an array:', artworks);
      return [];
    }
    return artworks.filter(artwork => artwork.galleryId === galleryId);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <AdminSectionLayout title="Device Management" description="Configure galleries, devices, and camera-to-artwork mappings.">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-600">Loading...</div>
          </div>
        </AdminSectionLayout>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <AdminSectionLayout
        title="Device Management"
        description="Configure galleries, devices, and camera-to-artwork mappings."
        actions={
          <button
            onClick={handleCreateNew}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Create New Configuration
          </button>
        }
      >
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-4xl max-h-[90vh] overflow-y-auto w-full">
              <h3 className="text-xl sm:text-2xl font-bold mb-4">
                {editingConfig ? 'Edit' : 'Create'} Gallery Configuration
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Gallery</label>
                    <select
                      value={formData.galleryId}
                      onChange={(e) => {
                        const selectedGallery = galleries.find(g => g.galleryId === e.target.value);
                        setFormData(prev => ({ 
                          ...prev, 
                          galleryId: e.target.value,
                          galleryName: selectedGallery ? selectedGallery.displayName : prev.galleryName
                        }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      disabled={editingConfig}
                    >
                      <option value="">Select a gallery</option>
                      {galleries.map((gallery) => (
                        <option key={gallery._id} value={gallery.galleryId}>
                          {gallery.displayName} ({gallery.galleryId})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Gallery Name</label>
                    <input
                      type="text"
                      value={formData.galleryName}
                      onChange={(e) => setFormData(prev => ({ ...prev, galleryName: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>

                <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-4">
                    <h4 className="text-lg font-semibold">Devices</h4>
                    <button
                      type="button"
                      onClick={addDevice}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Add Device
                    </button>
                  </div>

                  {formData.devices.map((device, deviceIndex) => (
                    <div key={deviceIndex} className="border rounded-lg p-4 mb-4 bg-gray-50">
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-4">
                        <h5 className="font-medium">Device {deviceIndex + 1}</h5>
                        <button
                          type="button"
                          onClick={() => removeDevice(deviceIndex)}
                        className="w-full sm:w-auto px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Remove Device
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">MAC Address</label>
                          <input
                            type="text"
                            value={device.macAddress}
                            onChange={(e) => updateDevice(deviceIndex, 'macAddress', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="fa:9a:b5:1a:33:33"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Device Name</label>
                          <input
                            type="text"
                            value={device.deviceName}
                            onChange={(e) => updateDevice(deviceIndex, 'deviceName', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="Raspberry Pi 1"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-2">
                          <h6 className="font-medium">Cameras</h6>
                          <button
                            type="button"
                            onClick={() => addCamera(deviceIndex)}
                            className="w-full sm:w-auto px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Add Camera
                          </button>
                        </div>

                        {device.cameras.map((camera, cameraIndex) => (
                          <div key={cameraIndex} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 mb-2 p-2 bg-white rounded">
                            <div className="sm:col-span-3">
                              <label className="block text-xs text-gray-600">Camera ID</label>
                              <input
                                type="number"
                                value={camera.cameraId}
                                onChange={(e) => updateCamera(deviceIndex, cameraIndex, 'cameraId', parseInt(e.target.value))}
                                className="w-full px-2 py-1 border rounded text-sm"
                                min="0"
                                required
                              />
                            </div>
                            <div className="sm:col-span-7">
                              <label className="block text-xs text-gray-600">Artwork (RaspID)</label>
                              <select
                                value={camera.artworkCameraId}
                                onChange={(e) => updateCamera(deviceIndex, cameraIndex, 'artworkCameraId', e.target.value)}
                                className="w-full px-2 py-1 border rounded text-sm"
                                required
                              >
                                <option value="">Select Artwork</option>
                                {getArtworkForGallery(formData.galleryId).map(artwork => (
                                  <option key={artwork._id} value={artwork.RaspID}>
                                    {artwork.title} (RaspID: {artwork.RaspID})
                                  </option>
                                ))}
                              </select>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeCamera(deviceIndex, cameraIndex)}
                              className="sm:col-span-2 px-2 py-1 bg-red-400 text-white rounded hover:bg-red-500 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="w-full sm:w-auto px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {editingConfig ? 'Update' : 'Create'} Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed">
              <thead className="sticky top-0 bg-gray-100 z-0">
                <tr>
                  <th className="px-4 sm:px-6 py-4 text-left font-semibold">Gallery</th>
                  <th className="px-4 sm:px-6 py-4 text-left font-semibold">Devices</th>
                  <th className="px-4 sm:px-6 py-4 text-left font-semibold">Cameras</th>
                  <th className="px-4 sm:px-6 py-4 text-left font-semibold">Status</th>
                  <th className="px-4 sm:px-6 py-4 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {galleryConfigs.map((config) => (
                  <tr key={config._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4">
                      <div>
                        <div className="font-semibold">{config.galleryName}</div>
                        <div className="text-sm text-gray-600">ID: {config.galleryId}</div>
                        {config.location && (
                          <div className="text-sm text-gray-500">{config.location}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="space-y-1">
                        {config.devices.map((device, index) => (
                          <div key={index} className="text-sm">
                            <div className="font-medium">{device.deviceName || 'Unnamed Device'}</div>
                            <div className="text-gray-600 font-mono break-all">{device.macAddress}</div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="text-sm">
                        {config.devices.reduce((total, device) => total + device.cameras.length, 0)} cameras
                        <div className="text-gray-600">
                          {config.devices.map((device, dIndex) => 
                            device.cameras.map((camera, cIndex) => (
                              <div key={`${dIndex}-${cIndex}`} className="text-xs">
                                Cam {camera.cameraId} → Art {camera.artworkCameraId}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        config.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {config.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => handleEdit(config)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => validateConfig(config.galleryId)}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                        >
                          Validate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {galleryConfigs.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-xl mb-4">No gallery configurations found</div>
            <button
              onClick={handleCreateNew}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Your First Configuration
            </button>
          </div>
        )}

        <CustomDialog
          isOpen={isDialogOpen}
          title={dialogData.title}
          message={dialogData.message}
          onConfirm={dialogData.onConfirm}
          onCancel={closeDialog}
        />
      </AdminSectionLayout>
    </>
  );
}
